import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE } from '../App';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';

const MEETINGS_PER_PAGE = 5;

const MT_COLORS = [
  { bg: 'rgba(241,255,92,0.2)',  color: '#8a9100' },
  { bg: 'rgba(98,240,255,0.2)',  color: '#007a8a' },
  { bg: 'rgba(189,255,192,0.3)', color: '#1a6b1a' },
  { bg: 'rgba(255,183,107,0.2)', color: '#8a5000' },
  { bg: 'rgba(189,107,255,0.2)', color: '#5a007a' },
  { bg: 'rgba(255,107,107,0.2)', color: '#8a0000' },
];

const DOC_ICON_COLORS = [
  'rgba(241,255,92,0.2)', 'rgba(189,255,192,0.3)', 'rgba(98,240,255,0.2)',
  'rgba(255,183,107,0.2)', 'rgba(189,107,255,0.2)', 'rgba(255,107,107,0.2)',
  'rgba(107,183,255,0.2)', 'rgba(255,241,107,0.2)',
];

function getMeetingTypeStyle(name, meetingTypesList) {
  const idx = meetingTypesList.findIndex(mt => mt.name === name);
  return MT_COLORS[idx % MT_COLORS.length] || MT_COLORS[0];
}

function getDocIconStyle(name, docTypesList) {
  const idx = docTypesList.findIndex(dt => dt.name === name);
  const bg = DOC_ICON_COLORS[idx % DOC_ICON_COLORS.length] || DOC_ICON_COLORS[0];
  const label = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3);
  return { bg, label };
}

function formatDate(dateStr) {
  const [year, month, day] = dateStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const dd      = String(date.getDate()).padStart(2, '0');
  const mm      = String(date.getMonth() + 1).padStart(2, '0');
  const yy      = String(date.getFullYear()).slice(-2);
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
  return `${weekday}, ${dd}/${mm}/${yy}`;
}

const SPECIAL_CHAR_RE = /[.*+?^${}()|[\]\\]/g;

function highlightText(text, q) {
  if (!text || !q) return text || '';
  const stripped = text.replace(/<\/?mark>/gi, '');
  const escaped  = q.replace(SPECIAL_CHAR_RE, '\\$&');
  return stripped.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
}

function getFileExt(filename) {
  return (filename || '').split('.').pop().toLowerCase();
}

function getPreviewMode(filename) {
  const ext = getFileExt(filename);
  if (ext === 'pdf') return 'pdf';
  if (['jpg','jpeg','png','gif','webp','bmp','svg'].includes(ext)) return 'image';
  if (['xlsx','xls'].includes(ext)) return 'xlsx';
  if (['doc','docx'].includes(ext)) return 'docx';
  if (['ppt','pptx'].includes(ext)) return 'pptx';
  if (['txt','csv','json','xml','md','html','htm'].includes(ext)) return 'text';
  return 'unsupported';
}

const selectStyle = {
  padding: '10px 12px', borderRadius: '8px', border: '0.5px solid #AAB0B8',
  fontSize: '13px', fontFamily: 'Manrope, sans-serif',
  color: '#22272D', background: '#fff', cursor: 'pointer',
};

function pageBtn(active) {
  return {
    padding: '6px 12px', borderRadius: '6px', fontSize: '13px',
    fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
    border:     active ? 'none' : '0.5px solid #AAB0B8',
    background: active ? '#F1FF5C' : 'transparent',
    color:      active ? '#22272D' : '#AAB0B8',
    fontWeight: active ? '600' : '400',
  };
}

function navBtn(disabled) {
  return {
    padding: '6px 16px', borderRadius: '6px', fontSize: '13px',
    fontFamily: 'Manrope, sans-serif', cursor: disabled ? 'not-allowed' : 'pointer',
    border: 'none', background: '#62F0FF', color: '#22272D',
    fontWeight: '600', opacity: disabled ? 0.4 : 1,
  };
}

// ── XLSX viewer ───────────────────────────────────────────────────────────────
function XlsxViewer({ url }) {
  const [sheets, setSheets]      = useState([]);
  const [activeSheet, setActive] = useState(0);
  const [headers, setHeaders]    = useState([]);
  const [rows, setRows]          = useState([]);
  const [loading, setLoading]    = useState(true);
  const [error, setError]        = useState('');
  const wbRef = React.useRef(null);

  useEffect(() => {
    setLoading(true);
    fetch(url, { credentials: 'include' })
      .then(r => r.arrayBuffer())
      .then(buf => {
        const wb = XLSX.read(buf, { type: 'array' });
        wbRef.current = wb;
        setSheets(wb.SheetNames);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        setHeaders(data[0] || []);
        setRows(data.slice(1));
        setLoading(false);
      })
      .catch(() => { setError('Failed to load spreadsheet.'); setLoading(false); });
  }, [url]);

  const handleSheetChange = (idx) => {
    setActive(idx);
    if (!wbRef.current) return;
    const ws = wbRef.current.Sheets[wbRef.current.SheetNames[idx]];
    const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    setHeaders(data[0] || []);
    setRows(data.slice(1));
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Loading spreadsheet…</div>;
  if (error)   return <div style={{ padding: '40px', textAlign: 'center', color: '#FF766B', fontFamily: 'Manrope, sans-serif' }}>{error}</div>;

  return (
    <div>
      {sheets.length > 1 && (
        <div style={{ display: 'flex', gap: '6px', padding: '12px 20px', borderBottom: '0.5px solid #AAB0B8', overflowX: 'auto' }}>
          {sheets.map((s, i) => (
            <button key={s} type="button" onClick={() => handleSheetChange(i)} style={{
              padding: '5px 14px', borderRadius: '6px', border: 'none', fontSize: '12px',
              fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
              background: i === activeSheet ? '#F1FF5C' : '#F4FAFF',
              color: '#22272D', fontWeight: i === activeSheet ? '600' : '400',
            }}>{s}</button>
          ))}
        </div>
      )}
      <div style={{ overflowX: 'auto', maxHeight: '65vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: 'Manrope, sans-serif' }}>
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} style={{
                    padding: '8px 12px', background: '#F4FAFF', color: '#22272D',
                    fontWeight: '600', textAlign: 'left', borderBottom: '1px solid #AAB0B8',
                    whiteSpace: 'nowrap', position: 'sticky', top: 0,
                  }}>{String(h)}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#F9FBFF' }}>
                {headers.map((_, ci) => (
                  <td key={ci} style={{
                    padding: '7px 12px', color: '#22272D',
                    borderBottom: '0.5px solid #E8ECEF', whiteSpace: 'nowrap',
                  }}>{String(row[ci] ?? '')}</td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={headers.length || 1} style={{ padding: '20px', textAlign: 'center', color: '#AAB0B8' }}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── DOCX viewer ───────────────────────────────────────────────────────────────
function DocxViewer({ url }) {
  const [html, setHtml]       = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    fetch(url, { credentials: 'include' })
      .then(r => r.arrayBuffer())
      .then(buf => mammoth.convertToHtml({ arrayBuffer: buf }))
      .then(result => { setHtml(result.value); setLoading(false); })
      .catch(() => { setError('Failed to load document.'); setLoading(false); });
  }, [url]);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Loading document…</div>;
  if (error)   return <div style={{ padding: '40px', textAlign: 'center', color: '#FF766B', fontFamily: 'Manrope, sans-serif' }}>{error}</div>;

  return (
    <div
      style={{ padding: '28px 36px', maxHeight: '65vh', overflowY: 'auto', fontSize: '13px', color: '#22272D', fontFamily: 'Georgia, serif', lineHeight: '1.7' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ── Text viewer ───────────────────────────────────────────────────────────────
function TextView({ url }) {
  const [text, setText]       = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(url, { credentials: 'include' })
      .then(r => r.text())
      .then(t => { setText(t); setLoading(false); })
      .catch(() => { setText('Failed to load file.'); setLoading(false); });
  }, [url]);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#AAB0B8' }}>Loading…</div>;

  return (
    <div style={{ padding: '20px', maxHeight: '65vh', overflowY: 'auto' }}>
      <pre style={{ fontSize: '12px', color: '#22272D', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{text}</pre>
    </div>
  );
}

// ── Preview Modal ─────────────────────────────────────────────────────────────
function PreviewModal({ doc, canDownload, onClose }) {
  const viewUrl = `${API_BASE}/api/view/${doc.filename}`;
  const mode    = getPreviewMode(doc.filename);

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const renderContent = () => {
    if (mode === 'pdf') {
      return <iframe src={viewUrl} title="Document Preview" style={{ width: '100%', height: '75vh', border: 'none', display: 'block' }} />;
    }
    if (mode === 'image') {
      return (
        <div style={{ padding: '20px', textAlign: 'center', background: '#F4FAFF', maxHeight: '75vh', overflowY: 'auto' }}>
          <img src={viewUrl} alt={doc.filename} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px', objectFit: 'contain' }} />
        </div>
      );
    }
    if (mode === 'xlsx') return <XlsxViewer url={viewUrl} />;
    if (mode === 'docx') return <DocxViewer url={viewUrl} />;
    if (mode === 'text') return <TextView url={viewUrl} />;
    if (mode === 'pptx') {
      return (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📑</div>
          <div style={{ fontSize: '14px', color: '#22272D', fontFamily: 'Manrope, sans-serif', marginBottom: '12px' }}>{doc.filename}</div>
          {canDownload && (
            <a href={`${API_BASE}/api/download/${doc.filename}`} style={{
              display: 'inline-block', padding: '10px 24px', borderRadius: '8px',
              background: '#F1FF5C', color: '#22272D', fontSize: '13px',
              fontWeight: '600', fontFamily: 'Manrope, sans-serif', textDecoration: 'none',
            }}>Download to view slides</a>
          )}
        </div>
      );
    }
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
        Preview not available for this file type.
        {canDownload && (
          <>
            <br /><br />
            <a href={`${API_BASE}/api/download/${doc.filename}`} style={{
              display: 'inline-block', padding: '10px 24px', borderRadius: '8px',
              background: '#F1FF5C', color: '#22272D', fontSize: '13px',
              fontWeight: '600', fontFamily: 'Manrope, sans-serif', textDecoration: 'none',
            }}>Download instead</a>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 2000,
      }}
    >
      <div style={{
        background: '#fff', borderRadius: '16px', width: '90vw', maxWidth: '960px',
        border: '0.5px solid #AAB0B8', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '0.5px solid #AAB0B8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fff',
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#22272D', fontFamily: 'Sora, sans-serif' }}>
              {doc.filename}
            </div>
            <div style={{ fontSize: '11px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif', marginTop: '2px' }}>
              {doc.type} · {doc.date}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {canDownload && (
              <a
                href={`${API_BASE}/api/download/${doc.filename}`}
                style={{
                  padding: '7px 16px', borderRadius: '8px', background: '#F1FF5C',
                  color: '#22272D', fontSize: '12px', fontWeight: '600',
                  fontFamily: 'Manrope, sans-serif', textDecoration: 'none',
                }}
              >
                Download
              </a>
            )}
            <button type="button" onClick={onClose} style={{
              width: '32px', height: '32px', borderRadius: '8px', border: '0.5px solid #AAB0B8',
              background: 'transparent', cursor: 'pointer', fontSize: '16px',
              color: '#AAB0B8', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>✕</button>
          </div>
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// ── DocActions ────────────────────────────────────────────────────────────────
function DocActions({ doc, canView, canDownload, canDelete, onDeleteClick, onViewClick }) {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      {canView && (
        <button
          type="button"
          onClick={() => onViewClick(doc)}
          style={{
            fontSize: '12px', padding: '6px 14px', borderRadius: '6px',
            border: 'none', background: '#8BFF9F', color: '#22272D',
            fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
          }}>
          View
        </button>
      )}
      {canDownload && (
        <a
          href={`${API_BASE}/api/download/${doc.filename}`}
          style={{
            fontSize: '12px', padding: '6px 14px', borderRadius: '6px',
            border: 'none', background: '#F1FF5C', color: '#22272D',
            textDecoration: 'none', fontWeight: '600', fontFamily: 'Manrope, sans-serif',
          }}>
          Download
        </a>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={() => onDeleteClick(doc)}
          style={{
            fontSize: '12px', padding: '6px 14px', borderRadius: '6px',
            border: 'none', background: '#FF766B', color: '#fff',
            cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontWeight: '600',
          }}>
          Delete
        </button>
      )}
    </div>
  );
}

// ── Documents ─────────────────────────────────────────────────────────────────
function Documents({ user }) {
  const [allMeetings,       setAllMeetings]       = useState([]);
  const [meetingTypesList,  setMeetingTypesList]  = useState([]);
  const [docTypesList,      setDocTypesList]      = useState([]);
  const [schedules,         setSchedules]         = useState([]);
  const [query,             setQuery]             = useState('');
  const [results,           setResults]           = useState([]);
  const [searched,          setSearched]          = useState(false);
  const [searching,         setSearching]         = useState(false);
  const [searchError,       setSearchError]       = useState('');
  const [currentPage,       setCurrentPage]       = useState(1);
  const [deleteModal,       setDeleteModal]       = useState(null);
  const [deletePassword,    setDeletePassword]    = useState('');
  const [deleteError,       setDeleteError]       = useState('');
  const [deleting,          setDeleting]          = useState(false);
  const [filterType,        setFilterType]        = useState('All');
  const [filterYear,        setFilterYear]        = useState('All');
  const [filterMeetingType, setFilterMeetingType] = useState('All');
  const [previewDoc,        setPreviewDoc]        = useState(null);

  // ── Permission flags (from logged-in user) ──────────────────────────────────
  const isSuper = user?.role === 'superadmin';
  const docPerms = (user?.permissions || {}).documents || {};
  const canView     = isSuper || !!docPerms.view;
  const canDownload = isSuper || !!docPerms.download;
  const hasDeletePerm = isSuper || !!docPerms.delete;
  const canDeleteDoc = (doc) => hasDeletePerm && (isSuper || doc.uploaded_by === user?.id);

  // Get schedule info by id
  const getScheduleInfo = (scheduleId) => {
    if (!scheduleId) return null;
    const sched = schedules.find((s) => s.id === scheduleId);
    return sched ? `${sched.meeting_type} — ${sched.date} at ${sched.time}` : null;
  };

  const loadMeetingTypes = useCallback(() => {
    axios.get(`${API_BASE}/api/meeting-types`, { withCredentials: true })
      .then(res => setMeetingTypesList(res.data))
      .catch(err => console.error('Failed to load meeting types:', err));
  }, []);
  useEffect(() => { loadMeetingTypes(); }, [loadMeetingTypes]);

  const loadDocTypes = useCallback(() => {
    axios.get(`${API_BASE}/api/document-types`, { withCredentials: true })
      .then(res => setDocTypesList(res.data))
      .catch(err => console.error('Failed to load document types:', err));
  }, []);
  useEffect(() => { loadDocTypes(); }, [loadDocTypes]);

  const loadSchedules = useCallback(() => {
    axios.get(`${API_BASE}/api/schedules`, { withCredentials: true })
      .then(res => setSchedules(res.data))
      .catch(err => console.error('Failed to load schedules:', err));
  }, []);
  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const loadDocuments = useCallback(() => {
    axios.get(`${API_BASE}/api/documents`, { withCredentials: true })
      .then(res => {
        const byDate = {};
        res.data.forEach(doc => {
          if (!byDate[doc.date]) byDate[doc.date] = [];
          byDate[doc.date].push(doc);
        });
        const sortedDates = Object.keys(byDate).sort((a, b) => new Date(b) - new Date(a));
        setAllMeetings(sortedDates.map(date => ({
          date,
          docs: byDate[date],
          meeting_type: byDate[date][0]?.meeting_type || 'Board Meeting',
        })));
      })
      .catch(err => console.error('Failed to load documents:', err));
  }, []);
  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true); setSearchError('');
    try {
      const [keywordRes, aiRes] = await Promise.all([
        axios.get(`${API_BASE}/api/search?q=${encodeURIComponent(query)}`,    { withCredentials: true }),
        axios.get(`${API_BASE}/api/ai-search?q=${encodeURIComponent(query)}`, { withCredentials: true }),
      ]);
      const seen = new Set(); const combined = [];
      keywordRes.data.forEach(r => { if (!seen.has(r.id)) { seen.add(r.id); combined.push({ ...r, snippet: highlightText(r.snippet, query), source: 'keyword' }); } });
      aiRes.data.forEach(r => { if (!seen.has(r.id)) { seen.add(r.id); combined.push({ ...r, snippet: highlightText(r.snippet, query), source: 'ai' }); } });
      setResults(combined); setSearched(true);
    } catch (err) {
      console.error('Search error:', err); setSearchError('Search failed. Please try again.');
    } finally { setSearching(false); }
  }, [query]);

  const clearSearch = useCallback(() => { setSearched(false); setResults([]); setQuery(''); setSearchError(''); }, []);

  const openDeleteModal  = useCallback((doc) => { setDeleteModal(doc); setDeletePassword(''); setDeleteError(''); }, []);
  const closeDeleteModal = useCallback(() => { setDeleteModal(null); setDeletePassword(''); setDeleteError(''); }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletePassword) { setDeleteError('Please enter your password'); return; }
    if (deleting) return;
    setDeleting(true); setDeleteError('');
    try {
      await axios.post(`${API_BASE}/api/documents/delete-confirm`, { doc_id: deleteModal.id, sa_password: deletePassword }, { withCredentials: true });
      closeDeleteModal(); loadDocuments();
    } catch (err) { setDeleteError(err.response?.data?.message || 'Incorrect password'); }
    finally { setDeleting(false); }
  }, [deletePassword, deleting, deleteModal, closeDeleteModal, loadDocuments]);

  const availableYears = useMemo(
    () => [...new Set(allMeetings.map(m => m.date.split('-')[0]))].sort((a, b) => b - a),
    [allMeetings],
  );

  const filteredMeetings = useMemo(() => (
    allMeetings
      .map(({ date, docs, meeting_type }) => ({
        date, meeting_type,
        docs: filterType === 'All' ? docs : docs.filter(d => d.type === filterType),
      }))
      .filter(({ date, docs, meeting_type }) => {
        const year = date.split('-')[0];
        return (
          (filterYear        === 'All' || year         === filterYear) &&
          (filterMeetingType === 'All' || meeting_type === filterMeetingType) &&
          docs.length > 0
        );
      })
  ), [allMeetings, filterType, filterYear, filterMeetingType]);

  const totalPages      = useMemo(() => Math.ceil(filteredMeetings.length / MEETINGS_PER_PAGE), [filteredMeetings]);
  const currentMeetings = useMemo(() => {
    const start = (currentPage - 1) * MEETINGS_PER_PAGE;
    return filteredMeetings.slice(start, start + MEETINGS_PER_PAGE);
  }, [filteredMeetings, currentPage]);

  const { groupedByYear, years } = useMemo(() => {
    const grouped = {};
    currentMeetings.forEach(({ date, docs, meeting_type }) => {
      const y = date.split('-')[0];
      if (!grouped[y]) grouped[y] = [];
      grouped[y].push({ date, docs, meeting_type });
    });
    return { groupedByYear: grouped, years: Object.keys(grouped).sort((a, b) => parseInt(b) - parseInt(a)) };
  }, [currentMeetings]);

  return (
    <div>
      <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '22px', fontWeight: '600', color: '#22272D', marginBottom: '24px' }}>
        Board Meeting Documents
      </h1>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '8px', flex: '1', minWidth: '260px', maxWidth: '480px' }}>
          <input
            type="text" placeholder="Search documents..." value={query}
            onChange={e => { setQuery(e.target.value); if (!e.target.value) clearSearch(); }}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{ flex: 1, padding: '10px 16px', border: '0.5px solid #AAB0B8', borderRadius: '8px', fontSize: '13px', fontFamily: 'Manrope, sans-serif', color: '#22272D', background: '#fff', outline: 'none', boxSizing: 'border-box' }}
          />
          <button type="button" onClick={handleSearch} disabled={searching || !query.trim()} style={{
            background: '#F1FF5C', color: '#22272D', border: 'none', padding: '10px 20px', borderRadius: '8px',
            fontSize: '14px', fontWeight: '600', fontFamily: 'Manrope, sans-serif',
            cursor: (searching || !query.trim()) ? 'not-allowed' : 'pointer', opacity: (searching || !query.trim()) ? 0.7 : 1,
          }}>{searching ? 'Searching…' : 'Search'}</button>
          {searched && (
            <button type="button" onClick={clearSearch} style={{
              background: 'transparent', color: '#AAB0B8', border: '0.5px solid #AAB0B8',
              padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontFamily: 'Manrope, sans-serif', cursor: 'pointer',
            }}>Clear</button>
          )}
        </div>

        {!searched && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={filterYear} onChange={e => { setFilterYear(e.target.value); setCurrentPage(1); }} style={selectStyle}>
              <option value="All">All Years</option>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={filterMeetingType} onChange={e => { setFilterMeetingType(e.target.value); setCurrentPage(1); }} style={selectStyle}>
              <option value="All">All Meeting Types</option>
              {meetingTypesList.map(mt => <option key={mt.id} value={mt.name}>{mt.name}</option>)}
            </select>
            <select value={filterType} onChange={e => { setFilterType(e.target.value); setCurrentPage(1); }} style={selectStyle}>
              <option value="All">All Doc Types</option>
              {docTypesList.map(dt => <option key={dt.id} value={dt.name}>{dt.name}</option>)}
            </select>
            {(filterYear !== 'All' || filterType !== 'All' || filterMeetingType !== 'All') && (
              <button type="button" onClick={() => { setFilterYear('All'); setFilterType('All'); setFilterMeetingType('All'); setCurrentPage(1); }} style={{
                padding: '10px 12px', borderRadius: '8px', border: '0.5px solid #AAB0B8',
                fontSize: '13px', fontFamily: 'Manrope, sans-serif', color: '#AAB0B8', background: 'transparent', cursor: 'pointer',
              }}>Clear Filters</button>
            )}
          </div>
        )}
      </div>

      {/* Search results */}
      {searched ? (
        <div>
          {searchError
            ? <p style={{ color: '#FF766B', fontSize: '13px', marginBottom: '16px' }}>{searchError}</p>
            : <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '16px' }}>{results.length} result(s) for &ldquo;{query}&rdquo;</p>
          }
          {results.length === 0 && !searchError && <p style={{ color: '#AAB0B8' }}>No documents found.</p>}
          {results.map(result => (
            <div key={result.id} style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#AAB0B8' }}>
                    {result.date} · {result.type}
                    {result.schedule_id && (
                      <div style={{ fontSize: '11px', color: '#3B9EBF', marginTop: '2px', fontWeight: '500' }}>
                        📅 {getScheduleInfo(result.schedule_id)}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: result.source === 'ai' ? 'rgba(98,240,255,0.2)' : 'rgba(241,255,92,0.2)', color: result.source === 'ai' ? '#00B4D8' : '#8a9100', fontWeight: '600' }}>
                  {result.source === 'ai' ? 'AI' : 'Keyword'}
                </div>
              </div>
              <div style={{ fontSize: '14px', color: '#22272D', fontWeight: '500', marginBottom: '10px' }}>{result.filename}</div>
              <div style={{ fontSize: '13px', color: '#22272D', lineHeight: '1.6', background: '#F4FAFF', padding: '10px', borderRadius: '6px' }} dangerouslySetInnerHTML={{ __html: result.snippet }} />
              <div style={{ marginTop: '12px' }}>
                <DocActions doc={result} canView={canView} canDownload={canDownload} canDelete={canDeleteDoc(result)} onDeleteClick={openDeleteModal} onViewClick={setPreviewDoc} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div>
          {allMeetings.length === 0 && <p style={{ color: '#AAB0B8' }}>No documents yet. Upload one to get started!</p>}

          {years.map(year => (
            <div key={year} style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '42px', fontWeight: '400', color: '#22272D', letterSpacing: '2px', lineHeight: 1 }}>{year}</div>
                <div style={{ flex: 1, height: '0.5px', background: '#AAB0B8', opacity: 0.4 }} />
                <div style={{ fontSize: '12px', color: '#AAB0B8' }}>{groupedByYear[year].length} meetings</div>
              </div>

              {groupedByYear[year].map(({ date, docs, meeting_type }) => {
                const mtStyle = getMeetingTypeStyle(meeting_type, meetingTypesList);
                return (
                  <div key={date} style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '20px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D' }}>{formatDate(date)}</span>
                        <span style={{ fontSize: '11px', background: mtStyle.bg, color: mtStyle.color, padding: '3px 10px', borderRadius: '20px', fontWeight: '600' }}>{meeting_type}</span>
                      </div>
                      <span style={{ fontSize: '11px', background: '#F1FF5C', color: '#22272D', padding: '3px 12px', borderRadius: '20px', fontWeight: '600' }}>{docs.length} docs</span>
                    </div>

                    {docs.map(doc => {
                      const icon = getDocIconStyle(doc.type, docTypesList);
                      return (
                        <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '0.5px solid #F4FAFF' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: icon.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: '#22272D', flexShrink: 0 }}>{icon.label}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '13px', color: '#22272D', fontWeight: '500' }}>{doc.filename}</div>
                              <div style={{ fontSize: '11px', color: '#AAB0B8' }}>
                                {doc.type}
                                {doc.uploaded_by_name ? ` · uploaded by ${doc.uploaded_by_name}` : ''}
                              </div>
                              {doc.schedule_id && (
                                <div style={{ fontSize: '11px', color: '#3B9EBF', marginTop: '2px', fontWeight: '500' }}>
                                  📅 {getScheduleInfo(doc.schedule_id)}
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0 }}>
                            <DocActions doc={doc} canView={canView} canDownload={canDownload} canDelete={canDeleteDoc(doc)} onDeleteClick={openDeleteModal} onViewClick={setPreviewDoc} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
              <button type="button" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={navBtn(currentPage === 1)}>← Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button type="button" key={page} onClick={() => setCurrentPage(page)} style={pageBtn(currentPage === page)}>{page}</button>
              ))}
              <button type="button" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={navBtn(currentPage === totalPages)}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && <PreviewModal doc={previewDoc} canDownload={canDownload} onClose={() => setPreviewDoc(null)} />}

      {/* Delete modal */}
      {deleteModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '420px', border: '0.5px solid #AAB0B8' }}>
            <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '18px', fontWeight: '600', color: '#22272D', marginBottom: '8px' }}>Delete document</h2>
            <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '6px' }}>You are about to permanently delete:</p>
            <p style={{ fontSize: '13px', color: '#22272D', fontWeight: '600', marginBottom: '6px' }}>{deleteModal.filename}</p>
            <p style={{ fontSize: '12px', color: '#FF766B', marginBottom: '20px' }}>This cannot be undone. Enter your password to confirm.</p>
            <label style={{ fontSize: '13px', color: '#22272D', fontWeight: '500', display: 'block', marginBottom: '6px' }}>Your password</label>
            <input
              type="password" autoComplete="new-password" value={deletePassword}
              onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleDeleteConfirm()}
              style={{ width: '100%', padding: '9px 12px', boxSizing: 'border-box', border: `0.5px solid ${deleteError ? '#FF766B' : '#AAB0B8'}`, borderRadius: '8px', fontSize: '13px', fontFamily: 'Manrope, sans-serif', color: '#22272D', background: '#F4FAFF', outline: 'none', marginBottom: '4px' }}
            />
            {deleteError && <div style={{ fontSize: '11px', color: '#FF766B', marginBottom: '8px' }}>{deleteError}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button type="button" onClick={handleDeleteConfirm} disabled={deleting} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#FF766B', color: '#fff', fontSize: '14px', fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1 }}>
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button type="button" onClick={closeDeleteModal} disabled={deleting} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '0.5px solid #AAB0B8', background: 'transparent', color: '#22272D', fontSize: '14px', fontFamily: 'Manrope, sans-serif', cursor: deleting ? 'not-allowed' : 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;